/**
 * EmailAutomationStopNotice
 *
 * The explanation at the top of a stopped email automation contact's modal:
 * what stopped the contact, when, and that no further steps will send. One
 * message per stop reason. Unsubscribed and removed contacts carry no reason,
 * and neither do contacts stopped before reasons were recorded, so those fall
 * back to the status. A contact that is still running, or finished normally,
 * gets no notice.
 *
 * Mirrors describeOutcome() on the SMS contacts page. Nothing here is ever
 * hard-deleted: every stop is a flag on the enrollment row, which keeps its
 * history and simply receives no further steps.
 */
import type { EmailAutomationStopReason } from '@/services/marketing.service';
import { formatPacificDateTime } from '@/lib/facilityTimezone';
import { cn } from '@/lib/utils';

/** The contact fields the notice reads. */
export interface EmailStopContact {
  status: string;
  stopReason?: EmailAutomationStopReason | string | null;
  /** Last write to the contact — when a stop happened. Pacific wall-clock. */
  updatedAt?: string | null;
  removedDate?: string | null;
}

export interface EmailStopDescription {
  tone: 'stop' | 'warn';
  title: string;
  whenLabel: string;
  when: string | null;
  lines: string[];
}

const NO_MORE = 'No further steps will send to this contact.';

/** The message for each recorded stop reason. */
const REASONS: Record<Exclude<EmailAutomationStopReason, ''>, { title: string; line: string }> = {
  blank: {
    title: 'No email address',
    line: 'This contact has no email address on file, so nothing was sent to them.',
  },
  malformed: {
    title: 'Email address is not valid',
    line: 'The address on file is not a valid email address (for example, it is missing its domain), so it was not sent to.',
  },
  flagged: {
    title: 'Address on the invalid-email list',
    line: "This address is on this account's invalid-email list — it bounced or was refused before — so it was not sent to.",
  },
  cleared: {
    title: "Patient's email was cleared",
    line: "The patient's email was cleared on their record, so nothing was sent to the old address this contact was added with.",
  },
  rejected: {
    title: 'Rejected by SendGrid',
    line: "SendGrid refused this address, so it was added to this account's invalid-email list. SendGrid's reason is on the failed send below.",
  },
  retries: {
    title: 'Stopped after 3 failed tries',
    line: "Three tries of the same step failed, so this contact was stopped. SendGrid's messages are on the failed sends below.",
  },
};

function isKnownReason(r: string): r is Exclude<EmailAutomationStopReason, ''> {
  return Object.prototype.hasOwnProperty.call(REASONS, r);
}

export function describeEmailStop(c: EmailStopContact): EmailStopDescription | null {
  const stoppedAt = c.updatedAt || null;

  if (c.status === 'active' || c.status === 'completed') return null;

  const reason = c.stopReason ?? '';
  if (isKnownReason(reason)) {
    const r = REASONS[reason];
    return { tone: 'stop', title: r.title, whenLabel: 'Stopped', when: stoppedAt, lines: [r.line, NO_MORE] };
  }

  switch (c.status) {
    case 'unsubscribed':
      return {
        tone: 'stop',
        title: 'Unsubscribed',
        whenLabel: 'Unsubscribed',
        when: stoppedAt,
        lines: ['This contact opted out of these emails.', NO_MORE],
      };
    case 'removed':
      return {
        tone: 'warn',
        title: 'Removed from the entry list',
        whenLabel: 'Removed',
        when: c.removedDate || stoppedAt,
        lines: [
          "This contact no longer matches the automation's entry list, so it left the automation. Removed means flagged, not deleted — its history below is kept.",
          NO_MORE,
        ],
      };
    // Invalid and Failed with no recorded reason: stopped before reasons were stored.
    case 'invalid':
      return {
        tone: 'stop',
        title: 'Unusable email address',
        whenLabel: 'Stopped',
        when: stoppedAt,
        lines: ["This contact's email address could not be used, so it was not sent to.", NO_MORE],
      };
    case 'failed':
      return {
        tone: 'stop',
        title: 'Stopped after failed tries',
        whenLabel: 'Stopped',
        when: stoppedAt,
        lines: ["Repeated tries failed, so this contact was stopped. SendGrid's messages are on the failed sends below.", NO_MORE],
      };
    default:
      return null;
  }
}

const TONE: Record<EmailStopDescription['tone'], string> = {
  stop: 'border-l-[#dc3545] bg-[#fff5f5]',
  warn: 'border-l-[#f0b429] bg-[#fffaf0]',
};

interface EmailAutomationStopNoticeProps {
  contact: EmailStopContact;
  /** The viewer's zone; contact dates are stored as Pacific wall-clock. */
  tz: string;
  className?: string;
}

export function EmailAutomationStopNotice({ contact, tz, className }: EmailAutomationStopNoticeProps) {
  const d = describeEmailStop(contact);
  if (!d) return null;

  return (
    <section
      aria-label="Why this contact stopped"
      className={cn('rounded-md border border-l-4 border-[#e5e5e5] px-3.5 py-3', TONE[d.tone], className)}
    >
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <span className="text-sm font-semibold text-[#333]">{d.title}</span>
        {d.when && (
          <span className="whitespace-nowrap text-xs text-[#888]">
            {d.whenLabel} {formatPacificDateTime(d.when, tz)}
          </span>
        )}
      </div>
      <ul className="m-0 list-disc pl-[18px]">
        {d.lines.map((line) => (
          <li key={line} className="mb-1 text-[13px] leading-[1.55] text-[#555] last:mb-0">{line}</li>
        ))}
      </ul>
    </section>
  );
}
